// src/pages/buyerNet/useBuyerNetForm.js
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useUserTemplate } from "./useUserTemplate";
import { useBuyerTemplateData } from "./useBuyerTemplateData";
import {
  parseCurrency,
  preserveAddressFields,
  computeCommission,
} from "../utils/buyerNet.shared";
import {
  computeClosingCostTotals,
  calculateDiscount,
} from "../utils/calculations";

export const useBuyerNetForm = (mainId, userId, companyId, setCompanyId) => {
  const [formData, setFormData] = useState({});
  const [templateformData, setTemplateformData] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { setValue, getValues, control, watch, reset } = useForm({
    defaultValues: {},
  });

  // Load template data directly (no Redux)
  const baseTemplate = useBuyerTemplateData(
    mainId && userId && companyId ? mainId : null,
    mainId && userId && companyId ? userId : null,
    mainId && userId && companyId ? companyId : null,
  );

  useEffect(() => {
    if (baseTemplate?.buyerSections) {
      // TEMP DEBUG — buyerSections pipeline trace, step 3: what reaches
      // templateformData (aliased as buyerTemplateData in ManualSheetSection)
      // right before the loan-type switch ever gets a chance to pick from it.
      // taxDebugLog("useBuyerNetForm: merging buyerSections into templateformData", {
      //   creditReportInCash: baseTemplate.buyerSections.cash?.find(
      //     (it) => it.key === "credit_report",
      //   ),
      // });
      setTemplateformData((prev) => ({
        ...(prev || {}),
        ...baseTemplate.buyerSections,
      }));
    }
  }, [baseTemplate]);


  // Initialize form from template
  useUserTemplate(
    mainId,
    userId,
    companyId,
    setFormData,
    formData,
    baseTemplate,
    reset,
  );

  // Commission + Discount computation when sales price or loan type changes
  useEffect(() => {
    if (isInitialLoad) return;
    const salesPrice = parseCurrency(formData.salesPrice);
    if (!salesPrice || salesPrice < 50000) return;
    if (formData?.addressVerified === false) return;

    const commissionEnabled = templateformData?.commission_buyer !== false;
    // Everything below now reads `prev` (this updater's own parameter)
    // instead of the `formData` closed over from this effect's own render.
    // That closure reflects whatever formData was when THIS effect run
    // started — not necessarily the true latest state if another update
    // (e.g. PropertyDetailsSection's loan-amount auto-populate effect,
    // which shares this same formData.salesPrice dependency and can run
    // in the same commit) is still pending. computeCommission/
    // calculateDiscount both shallow-spread whatever formData they're
    // given, and preserveAddressFields only re-merges addressMeta/
    // loanInfoMeta from `prev` — any OTHER loanInfoMeta field the stale
    // spread carries forward (requestedDownPayment, buyerDownPayment, …)
    // would still win over prev's fresher value once merged back in.
    let isOwnerCarry = false;
    setFormData((prev) => {
      const computedCommission = commissionEnabled
        ? computeCommission(prev, true)
        : prev;
      const discountData = calculateDiscount(prev, true);

      let conventionalPercent = prev?.loanInfoMeta?.conventionalPercent;

      if (prev?.loanInfoMeta?.buyersLoanType === "Owner Carry") {
        conventionalPercent = "80.00";
        isOwnerCarry = true;
      } else if (prev?.loanInfoMeta?.buyersLoanType === "Conventional") {
        conventionalPercent =
          prev?.loanInfoMeta?.conventionalPercent ||
          templateformData?.dataNational?.conventional_loans
            ?.conventional_loan_defaults?.resale_conventional_loan_ltv_percent ||
          "90.00";
      }
      // Cash / FHA / VA / USDA don't use conventionalPercent for their own
      // calculations, but the field must survive the round trip so switching
      // back to Conventional/Owner Carry restores what the user actually typed
      // instead of falling back to a template default.
      conventionalPercent = conventionalPercent ?? "0.00";

      const merged = {
        ...computedCommission,
        phase: "computed",
        loanInfoMeta: {
          ...(computedCommission.loanInfoMeta || {}),
          conventionalPercent,
          allowOwnerCarry1Check: isOwnerCarry,
          allowOwnerCarry2Check: false,
        },
        titleSettlementMeta: {
          ...(computedCommission.titleSettlementMeta || {}),
          discount: discountData.discountPercent,
          discountAmount: discountData.discountAmount,
        },
      };

      return preserveAddressFields(prev, merged);
    });

    setValue("loanInfoMeta.allowOwnerCarry1Check", isOwnerCarry);
    setValue("loanInfoMeta.allowOwnerCarry2Check", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.salesPrice,
    formData?.loanInfoMeta?.buyersLoanType,
    templateformData,
    isInitialLoad,
    setValue,
  ]);

  // Closing costs recompute when sales price or loan type changes
  useEffect(() => {
    const salesPrice = parseCurrency(formData.salesPrice);

    // TEMP DEBUG — remove once the closing-costs-not-loading investigation is closed.
    // taxDebugLog("useBuyerNetForm closing costs recompute effect firing", {
    //   salesPrice,
    //   addressVerified: formData?.addressVerified,
    //   closingCostsMetaLength: Array.isArray(formData?.closingCostsMeta)
    //     ? formData.closingCostsMeta.length
    //     : "not-an-array",
    //   closingCostsMetaSample: formData?.closingCostsMeta?.[0],
    //   buyersLoanType: formData?.loanInfoMeta?.buyersLoanType,
    // });

    if (!salesPrice || salesPrice < 50000) return;
    if (formData?.addressVerified === false) return;

    if (
      Array.isArray(formData?.closingCostsMeta) &&
      formData.closingCostsMeta.length > 0
    ) {
      setFormData((prev) => {
        const updated = computeClosingCostTotals(prev);
        // TEMP DEBUG — remove once the closing-costs-not-loading investigation is closed.
        // taxDebugLog("useBuyerNetForm closing costs recompute result", {
        //   updatedSample: updated?.closingCostsMeta?.[0],
        // });
        const loanTypeChanged =
          updated.loanInfoMeta?.buyersLoanType !==
          prev.loanInfoMeta?.buyersLoanType;

        if (
          !loanTypeChanged &&
          JSON.stringify(updated.closingCostsMeta) ===
          JSON.stringify(prev.closingCostsMeta)
        ) {
          return prev;
        }

        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.salesPrice,
    formData?.loanInfoMeta?.buyersLoanType,
    formData?.closingCostsMeta,
  ]);

  // Reset form data
  const resetForm = useCallback(() => {
    setFormData({});
    setTemplateformData(null);
    setIsInitialLoad(true);
  }, []);

  // Reset data to template defaults
  const resetData = useCallback((buyerSections) => {
    const titleSettlementMeta = {
      ownersTitleAmount: "0.00",
      ownersTitleBuyer: "0.00",
      ownersTitlePayor: "Seller",
      escrowSettlementPayor: "Seller",
      escrowSettlementAmount: "0.00",
      escrowSettlementBuyer: "0.00",
      escrowSettlementSplit: "0.00",
      lendersTitleAmount: "0.00",
      lendersTitleBuyer: "0.00",
      lendersTitlePayor: "Seller",
      discount: "0.00",
      discountAmount: "0.00",
    };

    const addressMeta = {
      address: "",
      unitNumber: "",
      street_no: "",
      street_name: "",
      city: "",
      state: "",
      county: "",
      zip: "",
      closing_state: "",
      closing_county: "",
      closing_zip_code: "",
    };

    const finalFormData = {
      addressMeta,
      addressVerified: false,
      preparedBy: "",
      preparedFor: "New Client",
      salesPrice: "0.00",
      phase: "finalFormData",
      escrowPremiumChanged: false,
      loanInfoMeta: {
        estimatedClosingDate: "",
        buyerDownPayment: "0.00",
        buyersLoanType: "Conventional",
        annualPropertyTax: "0.00",
        assessorsTaxRate: "0.00",
        autoEstimateLoan: false,
        conventionalPercent: "80.00",
        conventionalPercent2: "0.00",
        interestRate1:
          buyerSections?.tenure?.find((it) => it.key === "todays_interest_rate")
            ?.value ?? "0.00",
        interestRate2: "0.00",
        termsInYears1:
          buyerSections?.tenure?.find(
            (it) => it.key === "terms_of_loans_in_years",
          )?.value ?? "0",
        termsInYears2: "0",
        amount: "0.00",
        amount2: "0.00",
        adjusted: "0.00",
        noPMICheck: false,
        showAPRCheck: false,
        allowOwnerCarry1Check: false,
        allowOwnerCarry2Check: false,
        addDueYear1: "0.00",
        addDueYear2: "0.00",
      },
      titleSettlementMeta,
      closingCostsMeta: buyerSections?.closingCosts || [],
      buyerClosingCostsMeta: buyerSections?.conventional || [],
      compensationMeta: {
        sellerBrokerCompensation: "3.00",
        sellerBrokerCompensationAmount: "0.00",
        buyerBrokerCompensation: "2.50",
        buyerBrokerCompensationAmount: "0.00",
        totalAgtAmount: "0.00",
      },
      prepaidsMeta: {
        monthsTaxes: "0",
        rate:
          buyerSections?.insurance?.find(
            (it) => it.key === "tax_rate_per_year_per_100_of_sale_price",
          )?.value ?? "0.00",
        taxesAmount: "0.00",
        monthsIns:
          buyerSections?.insurance?.find(
            (it) => it.key === "number_of_months_insurance_prepaid",
          )?.value ?? "0.00",
        rateThou:
          buyerSections?.insurance?.find(
            (it) =>
              it.key ===
              "homeowner_insurance_rate_per_year_per_1000_of_sale_price",
          )?.value ?? "0.00",
        insAmount: "0.00",
        daysInterest: buyerSections?.tenure?.find((it) => it.key === "number_of_days_per_month")
            ?.value ?? "0.00",
        daysInterestAmount: "0.00",
        twoMonthsPMI: "0.00",
        fhaMip: "0.00",
        fhaMipCheck: true,
        fhaMipAmount: "0.00",
        FhaMipFin: "0.00",
        FhaMipFin2: "0.00",
        vaffFinance: "0.00",
        vaffFinanceCheck: true,
        vaffFinanceRate: "0.00",
        vaffFinanceAmount: "0.00",
        mipFinance: "0.00",
        mipFinanceCheck: true,
        mipFinanceAmount: "0.00",
        costs:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.label ?? "Other",
        costsAmount:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.value ?? "0.00",
        totalPrepaidItems: "0.00",
      },
      paymentMeta: {
        principalAndInterest: "0.00",
        realEstateTaxes: "0.00",
        homeownerInsurance: "0.00",
        rateMonthlyPMI: "0.00",
        rateMonthlyPMIAmount: "0.00",
        pAndI2ndTDAmount: "0.00",
        monthlyExpense1:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.label ??
          "Other",
        monthlyExpense2:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.label ??
          "Other",
        monthlyExpense1Amount:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.value ??
          "0.00",
        monthlyExpense2Amount:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.value ??
          "0.00",
        totalMonthlyPayment: "0.00",
        estimatedTaxProrations: "0.00",
      },
      totalClosingMeta: {
        totalInvestment: "0.00",
        totalCommission: "0.00",
        totalClosingCosts: "0.00",
        totalPrepaids: "0.00",
        totalMonthlyPayment: "0.00",
      },
    };

    setFormData(finalFormData);
  }, []);

  return {
    formData,
    setFormData,
    templateformData,
    setTemplateformData,
    isEditLoading,
    setIsEditLoading,
    isInitialLoad,
    setIsInitialLoad,
    setValue,
    getValues,
    resetForm,
    resetData,
    setCompanyId,
    control,
    watch,
    reset,
  };
};
